import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, unref as _unref, vModelRadio as _vModelRadio, withDirectives as _withDirectives, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "create-ticket-modal__title-meta" }
const _hoisted_2 = { class: "create-ticket-modal__step-indicator" }
const _hoisted_3 = {
  key: 0,
  class: "create-ticket-modal__step"
}
const _hoisted_4 = { class: "create-ticket-modal__founders" }
const _hoisted_5 = { class: "create-ticket-modal__founders-lead" }
const _hoisted_6 = { class: "create-ticket-modal__founders-list" }
const _hoisted_7 = ["href", "aria-label"]
const _hoisted_8 = { class: "create-ticket-modal__founder-text" }
const _hoisted_9 = { class: "create-ticket-modal__founder-handle" }
const _hoisted_10 = { class: "create-ticket-modal__founders-divider" }
const _hoisted_11 = {
  class: "create-ticket-modal__categories",
  role: "radiogroup",
  "aria-labelledby": "ticket-category-label"
}
const _hoisted_12 = ["value"]
const _hoisted_13 = { class: "create-ticket-modal__category-text" }
const _hoisted_14 = {
  key: 1,
  class: "create-ticket-modal__step"
}
const _hoisted_15 = {
  key: 0,
  class: "create-ticket-modal__context-card"
}
const _hoisted_16 = { class: "create-ticket-modal__context-row" }
const _hoisted_17 = { class: "create-ticket-modal__context-row" }
const _hoisted_18 = {
  key: 2,
  class: "create-ticket-modal__step"
}

import { computed, ref, watch } from 'vue';
import {
    CONTEXT_TYPE_LABELS,
    SUPPORT_ATTACHMENT_ACCEPT,
    SUPPORT_ATTACHMENT_MAX_PER_MESSAGE,
    SupportApiError,
    TICKET_CATEGORIES,
    createTicket,
    validateAttachmentList,
    type CreateTicketContext,
    type CreateTicketResult,
    type TicketCategory,
} from '@/services/support';
import { emitToast } from '@/shared/lib/toast';
import { useStore } from 'vuex';
import BetaBadge from '@/components/ui/BetaBadge.vue';
import UserAvatar from '@/components/common/UserAvatar.vue';
import {
    FOUNDERS,
    buildFounderMessage,
    buildFounderTelegramUrl,
} from '@/shared/lib/founderContact';

const COPY_TOAST_COOLDOWN_MS = 2000;

export default /*@__PURE__*/_defineComponent({
  __name: 'CreateTicketModal',
  props: {
    modelValue: { type: Boolean },
    context: {},
    presetCategory: {},
    parentTicketPublicId: {}
  },
  emits: ["update:modelValue", "created", "duplicate"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const currentStep = ref<number>(1);
const category = ref<TicketCategory | null>(null);
const subject = ref<string>('');
const description = ref<string>('');
const attachments = ref<File[]>([]);
const loading = ref<boolean>(false);
const generalError = ref<string>('');
const fieldErrors = ref<Record<string, string[]>>({});
const formRef = ref<{ validate: () => Promise<{ valid: boolean }> } | null>(null);

const categoryOptions = TICKET_CATEGORIES;
const acceptList = SUPPORT_ATTACHMENT_ACCEPT;

// Шаг 2 («Связанные данные») имеет смысл только когда обращение привязано к
// контексту (личная сессия / платёж / сессия). Для общего обращения шаг
// пропускаем — раньше там была пустая плашка-пустышка (баг 75).
const hasContext = computed<boolean>(() => props.context != null);
const TOTAL_STEPS = computed<number>(() => (hasContext.value ? 3 : 2));

const contextTypeLabel = computed<string>(() => {
    if (!props.context) return '';
    return CONTEXT_TYPE_LABELS[props.context.type] || props.context.type;
});

/* ─────────────────── Прямой контакт с фаундерами ─────────────────── */

// Стор читаем защищённо: модалка монтируется и там, где стора нет (спеки), и
// после clearUser (guards, catch fetchUser) — тогда user === null, а черновик
// просто теряет строку с именем.
const store = useStore();
const currentUser = computed<Record<string, unknown> | null>(
    () => (store?.state?.user as Record<string, unknown> | null) ?? null,
);

// Свой lookup, а не contextTypeLabel: тот при промахе справочника отдаёт
// машинное значение (`order_item`), и оно уехало бы в личку фаундеру.
const founderCategoryLabel = computed<string | null>(() => {
    if (!category.value) return null;
    return categoryOptions.find((opt) => opt.value === category.value)?.label ?? null;
});

const founderContextTypeLabel = computed<string | null>(() => {
    if (!props.context) return null;
    return CONTEXT_TYPE_LABELS[props.context.type] ?? null;
});

const founderMessage = computed<string>(() =>
    buildFounderMessage({
        firstName: currentUser.value?.first_name as string | undefined,
        lastName: currentUser.value?.last_name as string | undefined,
        userId: currentUser.value?.id as string | undefined,
        categoryLabel: founderCategoryLabel.value,
        contextTypeLabel: founderContextTypeLabel.value,
        contextId: props.context?.id ?? null,
    }),
);

const founderLinks = computed(() =>
    FOUNDERS.map((founder) => ({
        ...founder,
        url: buildFounderTelegramUrl(founder.handle, founderMessage.value),
    })),
);

/** Два тоста подряд по 4 секунды — это спам; запись в буфер идемпотентна. */
let lastCopyToastAt = 0;

/**
 * Копирование черновика в буфер как сайд-эффект перехода по ссылке.
 *
 * ЗДЕСЬ ЗАПРЕЩЁН `await` и `async`: WebKit привязывает право на запись в буфер
 * к user activation, и первый же await его съедает. По той же причине не
 * подменяем ссылку на window.open с preventDefault — попап-блокировщик срежет
 * переход. Переход выполняет сам <a>, мы только копируем.
 */
const onFounderClick = (): void => {
    const text = founderMessage.value;
    if (typeof navigator === 'undefined' || !navigator.clipboard?.writeText) return;

    let writing: Promise<void>;
    try {
        writing = navigator.clipboard.writeText(text);
    } catch {
        // Экзотические WebView умеют бросать синхронно.
        return;
    }

    writing.then(
        () => {
            // Вкладка Telegram уже в фокусе — тост в покинутой вкладке никто не
            // увидит, а смысл продублирован статичной подписью под карточками.
            if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
            const now = Date.now();
            if (now - lastCopyToastAt < COPY_TOAST_COOLDOWN_MS) return;
            lastCopyToastAt = now;
            emitToast(
                'Текст обращения скопирован в буфер обмена — вставьте, если не подставился сам',
            );
        },
        // Отказ буфера молчит: переход в Telegram уже состоялся, врать тостом нельзя.
        () => undefined,
    );
};

const subjectRule = (value: string): true | string =>
    (!!value && value.trim().length >= 3) || 'Тема от 3 символов.';

const descriptionRule = (value: string): true | string =>
    (!!value && value.trim().length >= 10) || 'Опишите проблему — минимум 10 символов.';

const attachmentError = computed<string>(() => {
    if (!attachments.value || attachments.value.length === 0) return '';
    return validateAttachmentList(attachments.value) ?? '';
});

const attachmentsRule = (value: File[]): true | string => {
    if (!value || value.length === 0) return true;
    if (value.length > SUPPORT_ATTACHMENT_MAX_PER_MESSAGE) {
        return `Максимум ${SUPPORT_ATTACHMENT_MAX_PER_MESSAGE} файлов.`;
    }
    return validateAttachmentList(value) ?? true;
};

const canAdvance = computed<boolean>(() => {
    if (currentStep.value === 1) {
        return category.value !== null;
    }
    if (currentStep.value === 2) {
        return true; // step 2 is informational
    }
    return false;
});

const canSubmit = computed<boolean>(() => {
    if (!category.value) return false;
    if (subject.value.trim().length < 3) return false;
    if (description.value.trim().length < 10) return false;
    if (attachmentError.value) return false;
    return true;
});

const reset = (): void => {
    currentStep.value = 1;
    category.value = props.presetCategory ?? null;
    subject.value = '';
    description.value = '';
    attachments.value = [];
    generalError.value = '';
    fieldErrors.value = {};
    loading.value = false;
};

watch(
    () => props.modelValue,
    (open) => {
        if (open) {
            reset();
        }
    },
    { immediate: true },
);

watch(
    () => props.presetCategory,
    (preset) => {
        // If host page changes its preselected category while the modal is closed,
        // pick it up next time the modal opens (via reset). When the modal is open,
        // honor the new value too — but only if the user hasn't already chosen.
        if (props.modelValue && preset && category.value === null) {
            category.value = preset;
        }
    },
);

const onDialogUpdate = (open: boolean): void => {
    if (!open && !loading.value) {
        emit('update:modelValue', false);
    }
};

const onCancel = (): void => {
    if (loading.value) return;
    emit('update:modelValue', false);
};

const goNext = (): void => {
    if (!canAdvance.value || currentStep.value >= TOTAL_STEPS.value) return;
    currentStep.value += 1;
};

const goBack = (): void => {
    if (currentStep.value <= 1) return;
    currentStep.value -= 1;
};

const onSubmit = async (): Promise<void> => {
    if (!canSubmit.value || loading.value) return;
    const validation = await formRef.value?.validate();
    if (validation && !validation.valid) {
        return;
    }
    loading.value = true;
    generalError.value = '';
    fieldErrors.value = {};
    try {
        if (!category.value) return;
        const result: CreateTicketResult = await createTicket({
            category: category.value,
            subject: subject.value.trim(),
            description: description.value.trim(),
            context: props.context ?? null,
            attachments: attachments.value.length ? Array.from(attachments.value) : undefined,
            parent_ticket_public_id: props.parentTicketPublicId ?? null,
        });
        const ticket = result.ticket;
        if (result.duplicate) {
            emit('duplicate', { publicId: ticket.public_id, subject: ticket.subject });
        } else {
            emit('created', { publicId: ticket.public_id, subject: ticket.subject });
            emitToast(`Обращение ${ticket.public_id} создано.`, 'success');
        }
        emit('update:modelValue', false);
    } catch (error) {
        if (error instanceof SupportApiError) {
            fieldErrors.value = error.fieldErrors;
            generalError.value = error.message;
        } else {
            generalError.value = (error as Error).message || 'Не удалось создать обращение.';
        }
    } finally {
        loading.value = false;
    }
};

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_progress_linear = _resolveComponent("v-progress-linear")!
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_textarea = _resolveComponent("v-textarea")!
  const _component_v_file_input = _resolveComponent("v-file-input")!
  const _component_v_alert = _resolveComponent("v-alert")!
  const _component_v_form = _resolveComponent("v-form")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_dialog = _resolveComponent("v-dialog")!

  return (_openBlock(), _createBlock(_component_v_dialog, {
    "model-value": _ctx.modelValue,
    "onUpdate:modelValue": onDialogUpdate,
    "max-width": "720",
    scrollable: "",
    persistent: ""
  }, {
    default: _withCtx(() => [
      _createVNode(_component_v_card, { class: "create-ticket-modal" }, {
        default: _withCtx(() => [
          _createVNode(_component_v_card_title, { class: "create-ticket-modal__title" }, {
            default: _withCtx(() => [
              _cache[5] || (_cache[5] = _createElementVNode("span", { class: "create-ticket-modal__title-text" }, "Сообщить о проблеме", -1)),
              _createElementVNode("span", _hoisted_1, [
                _createElementVNode("span", _hoisted_2, " Шаг " + _toDisplayString(currentStep.value) + " из " + _toDisplayString(TOTAL_STEPS.value), 1),
                _createVNode(_component_v_btn, {
                  icon: "",
                  variant: "text",
                  size: "small",
                  class: "create-ticket-modal__close",
                  "aria-label": "Закрыть",
                  disabled: loading.value,
                  onClick: onCancel
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_icon, null, {
                      default: _withCtx(() => _cache[4] || (_cache[4] = [
                        _createTextVNode("mdi-close")
                      ])),
                      _: 1
                    })
                  ]),
                  _: 1
                }, 8, ["disabled"])
              ])
            ]),
            _: 1
          }),
          _createVNode(_component_v_progress_linear, {
            "model-value": (currentStep.value / TOTAL_STEPS.value) * 100,
            color: "primary",
            height: "3",
            class: "create-ticket-modal__progress"
          }, null, 8, ["model-value"]),
          _createVNode(_component_v_card_text, { class: "create-ticket-modal__body" }, {
            default: _withCtx(() => [
              (currentStep.value === 1)
                ? (_openBlock(), _createElementBlock("section", _hoisted_3, [
                    _cache[10] || (_cache[10] = _createElementVNode("h3", { class: "create-ticket-modal__heading" }, "Что у вас случилось?", -1)),
                    _createElementVNode("div", _hoisted_4, [
                      _createElementVNode("p", _hoisted_5, [
                        _createVNode(_component_v_icon, {
                          size: "20",
                          color: "#0088cc",
                          class: "mr-1",
                          "aria-hidden": "true"
                        }, {
                          default: _withCtx(() => _cache[6] || (_cache[6] = [
                            _createTextVNode(" mdi-hand-wave ")
                          ])),
                          _: 1
                        }),
                        _cache[7] || (_cache[7] = _createTextVNode(" Напишите напрямую фаундерам — мы читаем каждое сообщение. "))
                      ]),
                      _createElementVNode("div", _hoisted_6, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(founderLinks.value, (founder) => {
                          return (_openBlock(), _createElementBlock("a", {
                            key: founder.handle,
                            href: founder.url,
                            target: "_blank",
                            rel: "noopener noreferrer",
                            class: "create-ticket-modal__founder-card",
                            "aria-label": `Написать ${founder.name} в Telegram, ${founder.handle} (откроется в новой вкладке)`,
                            onClick: onFounderClick,
                            onAuxclick: onFounderClick
                          }, [
                            _createVNode(UserAvatar, {
                              "avatar-url": founder.avatar,
                              "display-name": founder.name,
                              size: 32,
                              alt: ""
                            }, null, 8, ["avatar-url", "display-name"]),
                            _createElementVNode("span", _hoisted_8, [
                              _createElementVNode("strong", null, _toDisplayString(founder.name), 1),
                              _createElementVNode("span", _hoisted_9, _toDisplayString(founder.handle), 1)
                            ])
                          ], 40, _hoisted_7))
                        }), 128))
                      ]),
                      _cache[8] || (_cache[8] = _createElementVNode("p", { class: "create-ticket-modal__founders-note" }, " Мы подставим ваше имя и ID в чат и скопируем текст в буфер обмена — останется описать проблему. ", -1))
                    ]),
                    _createElementVNode("div", _hoisted_10, [
                      _createElementVNode("span", null, [
                        _cache[9] || (_cache[9] = _createTextVNode(" или оставьте обращение на платформе ")),
                        _createVNode(BetaBadge)
                      ])
                    ]),
                    _cache[11] || (_cache[11] = _createElementVNode("h4", {
                      id: "ticket-category-label",
                      class: "create-ticket-modal__subheading"
                    }, " Выберите категорию ", -1)),
                    _createElementVNode("ul", _hoisted_11, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(categoryOptions), (opt) => {
                        return (_openBlock(), _createElementBlock("li", {
                          key: opt.value,
                          class: _normalizeClass(["create-ticket-modal__category", { 'is-selected': category.value === opt.value }])
                        }, [
                          _createElementVNode("label", null, [
                            _withDirectives(_createElementVNode("input", {
                              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((category).value = $event)),
                              type: "radio",
                              name: "ticket-category",
                              value: opt.value,
                              class: "create-ticket-modal__radio"
                            }, null, 8, _hoisted_12), [
                              [_vModelRadio, category.value]
                            ]),
                            _createElementVNode("div", _hoisted_13, [
                              _createElementVNode("strong", null, _toDisplayString(opt.label), 1),
                              _createElementVNode("span", null, _toDisplayString(opt.description), 1)
                            ])
                          ])
                        ], 2))
                      }), 128))
                    ])
                  ]))
                : (hasContext.value && currentStep.value === 2)
                  ? (_openBlock(), _createElementBlock("section", _hoisted_14, [
                      _cache[16] || (_cache[16] = _createElementVNode("h3", { class: "create-ticket-modal__heading" }, "Связанные данные", -1)),
                      (_ctx.context)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                            _cache[14] || (_cache[14] = _createElementVNode("p", { class: "create-ticket-modal__hint" }, " Мы автоматически прикрепим к обращению: ", -1)),
                            _createElementVNode("div", _hoisted_16, [
                              _cache[12] || (_cache[12] = _createElementVNode("span", { class: "create-ticket-modal__context-label" }, "Тип:", -1)),
                              _createElementVNode("strong", null, _toDisplayString(contextTypeLabel.value), 1)
                            ]),
                            _createElementVNode("div", _hoisted_17, [
                              _cache[13] || (_cache[13] = _createElementVNode("span", { class: "create-ticket-modal__context-label" }, "ID:", -1)),
                              _createElementVNode("span", null, _toDisplayString(_ctx.context.id), 1)
                            ]),
                            _cache[15] || (_cache[15] = _createElementVNode("p", { class: "create-ticket-modal__context-note" }, " Эксперт, оплата и другие детали подгрузятся автоматически на стороне сервера. ", -1))
                          ]))
                        : _createCommentVNode("", true)
                    ]))
                  : (_openBlock(), _createElementBlock("section", _hoisted_18, [
                      _cache[18] || (_cache[18] = _createElementVNode("h3", { class: "create-ticket-modal__heading" }, "Опишите проблему", -1)),
                      _createVNode(_component_v_form, {
                        ref_key: "formRef",
                        ref: formRef
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_text_field, {
                            modelValue: subject.value,
                            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((subject).value = $event)),
                            label: "Тема",
                            variant: "outlined",
                            density: "comfortable",
                            rules: [subjectRule],
                            "error-messages": fieldErrors.value.subject,
                            counter: "120",
                            maxlength: 120
                          }, null, 8, ["modelValue", "rules", "error-messages"]),
                          _createVNode(_component_v_textarea, {
                            modelValue: description.value,
                            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((description).value = $event)),
                            label: "Что случилось?",
                            variant: "outlined",
                            rows: "6",
                            "auto-grow": "",
                            rules: [descriptionRule],
                            "error-messages": fieldErrors.value.description,
                            counter: "4000",
                            maxlength: 4000,
                            hint: "Минимум 10 символов. Чем подробнее опишете, тем быстрее мы поможем.",
                            "persistent-hint": ""
                          }, null, 8, ["modelValue", "rules", "error-messages"]),
                          _createVNode(_component_v_file_input, {
                            modelValue: attachments.value,
                            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((attachments).value = $event)),
                            label: "Прикрепить файлы (необязательно)",
                            variant: "outlined",
                            density: "comfortable",
                            multiple: "",
                            chips: "",
                            "show-size": "",
                            accept: _unref(acceptList),
                            "prepend-icon": "",
                            "prepend-inner-icon": "mdi-paperclip",
                            rules: [attachmentsRule],
                            "error-messages": 
                                attachmentError.value ? [attachmentError.value] : fieldErrors.value.attachments
                            ,
                            class: "mt-2"
                          }, null, 8, ["modelValue", "accept", "rules", "error-messages"]),
                          _cache[17] || (_cache[17] = _createElementVNode("p", { class: "create-ticket-modal__limits" }, " До 10 файлов, по 25 МБ каждый. Изображения, PDF, документы Word/Excel, TXT. Видео не поддерживаются. ", -1)),
                          (generalError.value)
                            ? (_openBlock(), _createBlock(_component_v_alert, {
                                key: 0,
                                type: "error",
                                variant: "tonal",
                                class: "mt-3"
                              }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(generalError.value), 1)
                                ]),
                                _: 1
                              }))
                            : _createCommentVNode("", true)
                        ]),
                        _: 1
                      }, 512)
                    ]))
            ]),
            _: 1
          }),
          _createVNode(_component_v_card_actions, { class: "create-ticket-modal__actions" }, {
            default: _withCtx(() => [
              _createVNode(_component_v_btn, {
                variant: "text",
                onClick: onCancel,
                disabled: loading.value
              }, {
                default: _withCtx(() => _cache[19] || (_cache[19] = [
                  _createTextVNode(" Отмена ")
                ])),
                _: 1
              }, 8, ["disabled"]),
              _createVNode(_component_v_spacer),
              (currentStep.value > 1)
                ? (_openBlock(), _createBlock(_component_v_btn, {
                    key: 0,
                    variant: "text",
                    onClick: goBack,
                    disabled: loading.value
                  }, {
                    default: _withCtx(() => _cache[20] || (_cache[20] = [
                      _createTextVNode(" Назад ")
                    ])),
                    _: 1
                  }, 8, ["disabled"]))
                : _createCommentVNode("", true),
              (currentStep.value < TOTAL_STEPS.value)
                ? (_openBlock(), _createBlock(_component_v_btn, {
                    key: 1,
                    color: "primary",
                    variant: "flat",
                    disabled: !canAdvance.value,
                    onClick: goNext
                  }, {
                    default: _withCtx(() => _cache[21] || (_cache[21] = [
                      _createTextVNode(" Далее ")
                    ])),
                    _: 1
                  }, 8, ["disabled"]))
                : (_openBlock(), _createBlock(_component_v_btn, {
                    key: 2,
                    color: "primary",
                    variant: "flat",
                    loading: loading.value,
                    disabled: !canSubmit.value || loading.value,
                    onClick: onSubmit
                  }, {
                    default: _withCtx(() => _cache[22] || (_cache[22] = [
                      _createTextVNode(" Отправить ")
                    ])),
                    _: 1
                  }, 8, ["loading", "disabled"]))
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["model-value"]))
}
}

})